import { useMessengerStoreConversationList } from "@faire/messenger/store/useMessengerStoreConversationList";
import { LazyHeaderAccount } from "@faire/retailer-visitor-shared/components/Header/HeaderAccount/index";
import { HeaderClickItem } from "@faire/retailer-visitor-shared/components/Header/HeaderClickItem";
import { IconWithMaybeDot } from "@faire/retailer-visitor-shared/components/Header/icons/IconWithMaybeDot";
import {
  HeaderDropdown,
  HeaderIconContainer,
} from "@faire/retailer-visitor-shared/components/Header/styled";
import { useIsHeaderDropdownVisible } from "@faire/retailer-visitor-shared/components/HeaderPriorityRenderer/useIsHeaderDropdownVisible";
import { useRevampedDropdownData } from "@faire/retailer-visitor-shared/hooks/accountMenu/useRevampedDropdownData";
import { useFairePayInfo } from "@faire/retailer-visitor-shared/hooks/fairePay/useFairePayInfo";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { Account } from "@faire/slate/icons/Account";
import { trackRetailerNotificationsUserAccountClick } from "@faire/web-api--source/events/retailerNotifications/click/userAccount";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Spacer } from "@faire/web/slate/spacing";
import { useViewportWidth } from "@faire/web/ui/hooks/useViewportWidth";
import React, { Suspense, useCallback, useState } from "react";

import { MAX_HEADER_WIDTH } from "../consts";

export const HeaderAccountDropdown = () => {
  const { strictLocalize } = useStrictLocalization();
  const retailer = getRetailer();
  const [shouldShowDropdown, setShouldShowDropdown] = useState(false);
  const { unreadConversationCount } = useMessengerStoreConversationList();
  const { setHeaderDropdownVisibility } = useIsHeaderDropdownVisible();
  const viewportWidth = useViewportWidth((width) => width);
  const { isPreEnrolled: isPreEnrolledFairePay } = useFairePayInfo();
  const showDot = unreadConversationCount > 0 || isPreEnrolledFairePay;

  // prefetch the data for the dropdown that is used in the child component
  useRevampedDropdownData();

  // place the dropdown at the right edge of the header
  const rightPosition =
    viewportWidth <= MAX_HEADER_WIDTH
      ? 0
      : (viewportWidth - MAX_HEADER_WIDTH) / 2;

  const toggleHandler = useCallback(
    (shouldShowDropdown: boolean) => {
      setShouldShowDropdown(shouldShowDropdown);
      setHeaderDropdownVisibility("ACCOUNT", shouldShowDropdown);
    },
    [setHeaderDropdownVisibility]
  );

  if (retailer === undefined) {
    return null;
  }

  const accountDropdown = (
    <HeaderDropdown
      $right={`${rightPosition}px`}
      $width="288px"
      $maxHeight="calc(100vh - 110px)"
    >
      <Suspense fallback={null}>
        <LazyHeaderAccount onItemClick={() => setShouldShowDropdown(false)} />
      </Suspense>
    </HeaderDropdown>
  );

  return (
    <>
      <HeaderClickItem
        clickChild={accountDropdown}
        dataTestId="accountDropdown"
        ariaLabel={strictLocalize({
          id: "q0sa+6",
          defaultMessage: "My Profile",
          description: { text: "Link to my profile page" },
        })}
        onClick={trackRetailerNotificationsUserAccountClick}
        toggleDropdown={toggleHandler}
        shouldShowDropdown={shouldShowDropdown}
      >
        <HeaderIconContainer>
          <IconWithMaybeDot
            icon={<Account strokeWidth={0.8} fontSize="24px" />}
            shouldAnimateDot
            shouldShowDot={showDot}
            isActive={shouldShowDropdown}
            delayAnimationTime={750}
            dataTestId="notification-dot"
          />
        </HeaderIconContainer>
      </HeaderClickItem>
      <Spacer size={12} />
    </>
  );
};
